/**
 * FormReviewWizard Component
 *
 * Phase 6.6: User-Uploaded Forms
 *
 * A user-friendly wizard for reviewing AI-analyzed field mappings
 * on contractor-uploaded custom forms.
 *
 * Features:
 * - Section-by-section review
 * - Quick-fix suggestions for low-confidence fields
 * - Progress tracking
 * - Simple approve workflow
 */

import { useState, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Sparkles,
  Search,
  FileText,
  CheckCircle2,
  Edit,
  Info,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useCustomForm,
  useUpdateFieldMapping,
  useApproveCustomForm,
  type CustomForm,
} from '@/hooks/useCustomForms';
import { FIELD_ONTOLOGY, type OntologyField, type FieldCategory } from '@/lib/form-ontology';
import type { ParsedFormField } from '@/types/smart-schema';

// =============================================================================
// TYPES
// =============================================================================

interface FormReviewWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formId: string;
  onApproved?: (form: CustomForm) => void;
}

type ReviewStep = 'overview' | 'section' | 'summary';

interface FieldWithReview extends ParsedFormField {
  isReviewed: boolean;
  wasCorrected: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const CATEGORY_LABELS: Record<FieldCategory, string> = {
  property: 'Property',
  owner: 'Owner',
  contractor: 'Contractor',
  project: 'Project',
  permit: 'Permit',
  surety: 'Surety',
  lender: 'Lender',
  dates: 'Dates',
  notary: 'Notary',
  signatures: 'Signatures',
  pool: 'Pool',
  electrical: 'Electrical',
  mechanical: 'Mechanical',
  plumbing: 'Plumbing',
  roofing: 'Roofing',
  private_provider: 'Private Provider',
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function getConfidenceColor(confidence: number): string {
  if (confidence >= 0.85) return 'text-green-500';
  if (confidence >= 0.6) return 'text-yellow-500';
  return 'text-red-500';
}

function getConfidenceBadge(confidence: number): { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string } {
  if (confidence >= 0.85) return { variant: 'default', label: 'High' };
  if (confidence >= 0.6) return { variant: 'secondary', label: 'Medium' };
  return { variant: 'destructive', label: 'Low' };
}

function getSectionName(sectionId: string): string {
  return sectionId
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
}

function calculateMatchScore(labelText: string, ontologyField: OntologyField): number {
  const normalizedLabel = labelText.toLowerCase().trim();

  if (normalizedLabel === ontologyField.fieldId.toLowerCase()) return 1.0;
  if (ontologyField.synonyms.some(s => s.toLowerCase() === normalizedLabel)) return 0.95;

  const partialMatches = ontologyField.synonyms.filter(s =>
    normalizedLabel.includes(s.toLowerCase()) || s.toLowerCase().includes(normalizedLabel)
  );
  if (partialMatches.length > 0) return 0.7;

  const labelWords = normalizedLabel.split(/\s+/);
  const synonymWords = ontologyField.synonyms.flatMap(s => s.toLowerCase().split(/\s+/));
  const overlap = labelWords.filter(w => synonymWords.includes(w)).length;
  if (overlap > 0) return 0.3 + (overlap / labelWords.length) * 0.3;

  return 0;
}

function getSuggestions(labelText: string, limit = 5): Array<{ fieldId: string; ontology: OntologyField; score: number }> {
  const suggestions: Array<{ fieldId: string; ontology: OntologyField; score: number }> = [];

  for (const [fieldId, ontology] of Object.entries(FIELD_ONTOLOGY)) {
    const score = calculateMatchScore(labelText, ontology);
    if (score > 0.1) {
      suggestions.push({ fieldId, ontology, score });
    }
  }

  return suggestions.sort((a, b) => b.score - a.score).slice(0, limit);
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

interface FieldReviewCardProps {
  field: ParsedFormField;
  isReviewed: boolean;
  onUpdate: (updates: Partial<ParsedFormField>) => void;
  onMarkReviewed: () => void;
}

function FieldReviewCard({ field, isReviewed, onUpdate, onMarkReviewed }: FieldReviewCardProps) {
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const currentOntology = field.semanticId !== 'unknown'
    ? FIELD_ONTOLOGY[field.semanticId]
    : null;

  const suggestions = useMemo(() => getSuggestions(field.label), [field.label]);
  const confidenceBadge = getConfidenceBadge(field.confidence);

  const filteredOntology = useMemo(() => {
    if (!searchTerm) return [];
    const term = searchTerm.toLowerCase();
    return Object.entries(FIELD_ONTOLOGY)
      .filter(([id, ont]) =>
        id.toLowerCase().includes(term) ||
        ont.displayLabel.toLowerCase().includes(term) ||
        ont.synonyms.some(s => s.toLowerCase().includes(term))
      )
      .slice(0, 10);
  }, [searchTerm]);

  const handleSemanticIdChange = (newId: string) => {
    onUpdate({
      semanticId: newId as ParsedFormField['semanticId'],
      confidence: 0.99, // Manual mapping = high confidence
    });
    setShowSearch(false);
    setSearchTerm('');
    onMarkReviewed();
  };

  return (
    <div className={cn(
      'rounded-lg border p-4 space-y-3 transition-colors',
      isReviewed
        ? 'border-green-500/30 bg-green-500/5'
        : field.confidence < 0.6
        ? 'border-yellow-500/30 bg-yellow-500/5'
        : 'border-border bg-card'
    )}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{field.label}</span>
            {isReviewed && (
              <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
            )}
          </div>
          <p className="text-sm text-muted-foreground font-mono truncate">
            {field.pdfField}
          </p>
        </div>
        <Badge variant={confidenceBadge.variant} className="flex-shrink-0">
          <Sparkles className="h-3 w-3 mr-1" />
          {Math.round(field.confidence * 100)}%
        </Badge>
      </div>

      {/* Current Mapping */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-muted-foreground">Mapped To</Label>
          {!isReviewed && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={() => setShowSearch(!showSearch)}
            >
              <Edit className="h-3 w-3 mr-1" />
              Change
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2 p-2 rounded-md bg-muted/50">
          {field.semanticId === 'unknown' ? (
            <span className="text-sm text-muted-foreground italic">
              No mapping (custom field)
            </span>
          ) : (
            <>
              <span className="text-sm font-medium">
                {currentOntology?.displayLabel || field.semanticId}
              </span>
              {currentOntology && (
                <Badge variant="outline" className="text-xs">
                  {CATEGORY_LABELS[currentOntology.category]}
                </Badge>
              )}
            </>
          )}
        </div>
      </div>

      {/* Search / Change Mapping */}
      {showSearch && (
        <div className="space-y-2 p-3 rounded-md bg-muted/30 border">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search field types..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-8 h-8 text-sm"
              autoFocus
            />
          </div>

          {/* Suggestions */}
          {!searchTerm && suggestions.length > 0 && (
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Suggestions</span>
              {suggestions.map(s => (
                <Button
                  key={s.fieldId}
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start h-auto py-1.5 px-2"
                  onClick={() => handleSemanticIdChange(s.fieldId)}
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="text-sm">{s.ontology.displayLabel}</span>
                    <Badge variant="outline" className="text-xs">
                      {CATEGORY_LABELS[s.ontology.category]}
                    </Badge>
                  </div>
                </Button>
              ))}
            </div>
          )}

          {/* Search Results */}
          {searchTerm && filteredOntology.length > 0 && (
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {filteredOntology.map(([id, ont]) => (
                <Button
                  key={id}
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start h-auto py-1.5 px-2"
                  onClick={() => handleSemanticIdChange(id)}
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="text-sm">{ont.displayLabel}</span>
                    <Badge variant="outline" className="text-xs">
                      {CATEGORY_LABELS[ont.category]}
                    </Badge>
                  </div>
                </Button>
              ))}
            </div>
          )}

          {searchTerm && filteredOntology.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-2">
              No matching fields found
            </p>
          )}

          <Button
            variant="ghost"
            size="sm"
            className="w-full text-muted-foreground"
            onClick={() => handleSemanticIdChange('unknown')}
          >
            Mark as custom field
          </Button>
        </div>
      )}

      {/* Low Confidence Warning */}
      {field.confidence < 0.6 && !isReviewed && (
        <Alert variant="default" className="bg-yellow-500/10 border-yellow-500/30">
          <AlertCircle className="h-4 w-4 text-yellow-500" />
          <AlertDescription className="text-xs">
            AI is uncertain about this mapping. Please verify it's correct.
          </AlertDescription>
        </Alert>
      )}

      {/* Quick Actions */}
      {!isReviewed && (
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={onMarkReviewed}
          >
            <Check className="h-4 w-4 mr-1" />
            Looks Good
          </Button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function FormReviewWizard({
  open,
  onOpenChange,
  formId,
  onApproved,
}: FormReviewWizardProps) {
  // Fetch form data
  const { data: form, isLoading, refetch } = useCustomForm(formId);
  const updateFieldMutation = useUpdateFieldMapping();
  const approveMutation = useApproveCustomForm();

  // State
  const [currentStep, setCurrentStep] = useState<ReviewStep>('overview');
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [reviewedFields, setReviewedFields] = useState<Set<string>>(new Set());

  // Derived data
  const sections = useMemo(() => {
    if (!form?.sections) return [];
    return form.sections.sort((a, b) => a.order - b.order);
  }, [form?.sections]);

  const fieldsNeedingReview = useMemo(() => {
    if (!form?.fields) return [];
    return form.fields.filter(f => f.confidence < 0.7 || f.semanticId === 'unknown');
  }, [form?.fields]);

  const totalFields = form?.fields?.length || 0;
  const reviewedCount = reviewedFields.size;
  const lowConfidenceCount = fieldsNeedingReview.length;

  const currentSection = sections[currentSectionIndex];
  const currentSectionFields = useMemo(() => {
    if (!form?.fields || !currentSection) return [];
    return form.fields.filter(f => f.section === currentSection.id);
  }, [form?.fields, currentSection]);

  const progress = totalFields > 0 ? (reviewedCount / totalFields) * 100 : 0;

  // Handlers
  const handleFieldUpdate = useCallback(async (
    fieldId: string,
    updates: Partial<ParsedFormField>
  ) => {
    if (!form) return;
    await updateFieldMutation.mutateAsync({
      formId: form.id,
      fieldId,
      updates,
    });
    refetch();
  }, [form, updateFieldMutation, refetch]);

  const handleMarkReviewed = useCallback((fieldId: string) => {
    setReviewedFields(prev => new Set(prev).add(fieldId));
  }, []);

  const handleApprove = async () => {
    if (!form) return;
    try {
      const approved = await approveMutation.mutateAsync(form.id);
      onApproved?.(approved);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to approve form:', error);
    }
  };

  const handleNextSection = () => {
    if (currentSectionIndex < sections.length - 1) {
      setCurrentSectionIndex(prev => prev + 1);
    } else {
      setCurrentStep('summary');
    }
  };

  const handlePrevSection = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(prev => prev - 1);
    } else {
      setCurrentStep('overview');
    }
  };

  // Render loading state
  if (isLoading || !form) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Render overview step
  const renderOverview = () => (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
          <FileText className="h-8 w-8 text-primary" />
        </div>
        <h3 className="text-lg font-semibold">{form.form_name}</h3>
        <p className="text-muted-foreground text-sm">
          AI has analyzed your form and identified {totalFields} fields.
          {lowConfidenceCount > 0 && (
            <span className="text-yellow-500">
              {' '}{lowConfidenceCount} fields need review.
            </span>
          )}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center p-4 rounded-lg bg-muted/50">
          <p className="text-2xl font-bold">{totalFields}</p>
          <p className="text-sm text-muted-foreground">Total Fields</p>
        </div>
        <div className="text-center p-4 rounded-lg bg-muted/50">
          <p className="text-2xl font-bold">{sections.length}</p>
          <p className="text-sm text-muted-foreground">Sections</p>
        </div>
        <div className="text-center p-4 rounded-lg bg-muted/50">
          <p className={cn(
            "text-2xl font-bold",
            (form.ai_confidence_avg || 0) >= 0.8 ? "text-green-500" : "text-yellow-500"
          )}>
            {Math.round((form.ai_confidence_avg || 0) * 100)}%
          </p>
          <p className="text-sm text-muted-foreground">AI Confidence</p>
        </div>
      </div>

      {/* Section Preview */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Sections to Review</Label>
        <div className="space-y-2">
          {sections.map((section, idx) => {
            const sectionFields = form.fields.filter(f => f.section === section.id);
            const lowConfFields = sectionFields.filter(f => f.confidence < 0.7);
            return (
              <div
                key={section.id}
                className="flex items-center justify-between p-3 rounded-md border"
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-muted-foreground">
                    {idx + 1}.
                  </span>
                  <span>{section.name}</span>
                  <Badge variant="outline">{sectionFields.length} fields</Badge>
                </div>
                {lowConfFields.length > 0 && (
                  <Badge variant="secondary" className="text-yellow-600 bg-yellow-500/10">
                    {lowConfFields.length} need review
                  </Badge>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Review Later
        </Button>
        <Button onClick={() => setCurrentStep('section')}>
          Start Review
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );

  // Render section review step
  const renderSectionReview = () => (
    <div className="space-y-4">
      {/* Progress */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Section {currentSectionIndex + 1} of {sections.length}
          </span>
          <span className="text-muted-foreground">
            {reviewedCount} of {totalFields} fields reviewed
          </span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Section Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{currentSection?.name}</h3>
        <Badge variant="outline">
          {currentSectionFields.length} fields
        </Badge>
      </div>

      {/* Fields */}
      <ScrollArea className="h-[400px] pr-4">
        <div className="space-y-4">
          {currentSectionFields.map(field => (
            <FieldReviewCard
              key={field.pdfField}
              field={field}
              isReviewed={reviewedFields.has(field.pdfField)}
              onUpdate={updates => handleFieldUpdate(field.pdfField, updates)}
              onMarkReviewed={() => handleMarkReviewed(field.pdfField)}
            />
          ))}
        </div>
      </ScrollArea>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button
          variant="outline"
          onClick={handlePrevSection}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          {currentSectionIndex === 0 ? 'Back to Overview' : 'Previous Section'}
        </Button>
        <Button onClick={handleNextSection}>
          {currentSectionIndex === sections.length - 1 ? 'Review Summary' : 'Next Section'}
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );

  // Render summary step
  const renderSummary = () => (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="h-16 w-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
          <CheckCircle2 className="h-8 w-8 text-green-500" />
        </div>
        <h3 className="text-lg font-semibold">Review Complete</h3>
        <p className="text-muted-foreground text-sm">
          You've reviewed your form. Ready to approve it for use?
        </p>
      </div>

      {/* Summary Stats */}
      <div className="p-4 rounded-lg bg-muted/50 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Fields Reviewed</span>
          <span className="font-medium">{reviewedCount} / {totalFields}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Overall Confidence</span>
          <span className={cn(
            "font-medium",
            (form.ai_confidence_avg || 0) >= 0.8 ? "text-green-500" : "text-yellow-500"
          )}>
            {Math.round((form.ai_confidence_avg || 0) * 100)}%
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Corrections Made</span>
          <span className="font-medium">{form.fields_corrected || 0}</span>
        </div>
      </div>

      {/* Warning if not all reviewed */}
      {reviewedCount < totalFields && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>Some fields weren't reviewed</AlertTitle>
          <AlertDescription>
            You can still approve the form. Unreviewed fields will use the AI's original mapping.
          </AlertDescription>
        </Alert>
      )}

      {/* Actions */}
      <div className="flex justify-between pt-4 border-t">
        <Button
          variant="outline"
          onClick={() => setCurrentStep('section')}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Review
        </Button>
        <Button
          onClick={handleApprove}
          disabled={approveMutation.isPending}
        >
          {approveMutation.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Check className="h-4 w-4 mr-1" />
          )}
          Approve Form
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Review Field Mappings
          </DialogTitle>
          <DialogDescription>
            Verify AI-detected fields match your form correctly. You can fix any mistakes.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden py-4">
          {currentStep === 'overview' && renderOverview()}
          {currentStep === 'section' && renderSectionReview()}
          {currentStep === 'summary' && renderSummary()}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default FormReviewWizard;
